import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { conversationApi, type Handoff, type ConversationDetail } from '@/lib/api';
import { AlertTriangle, MessageSquare, Clock, User, CheckCircle2, ExternalLink, RefreshCw } from 'lucide-react';
import { PageGuide } from '@/components/PageGuide';
import { Spinner } from '@/components/ui/Spinner';
import { usePortalSocketEvent } from '@/contexts/PortalSocketContext';

export const Route = createFileRoute('/_auth/handoffs')({
  component: HandoffsPage,
});

type ConvWithHandoff = ConversationDetail & { handoff: Handoff };

const PRIORITY_STYLE: Record<string, { bg: string; text: string; border: string }> = {
  LOW:    { bg: '#64748b15', text: '#94a3b8', border: '#64748b30' },
  MEDIUM: { bg: '#f59e0b15', text: '#fbbf24', border: '#f59e0b30' },
  HIGH:   { bg: '#ef444415', text: '#f87171', border: '#ef444430' },
  URGENT: { bg: '#ef444420', text: '#f87171', border: '#ef444450' },
};

const STATUS_STYLE: Record<string, { bg: string; text: string; border: string }> = {
  OPEN:     { bg: '#f59e0b15', text: '#fbbf24', border: '#f59e0b30' },
  ASSIGNED: { bg: '#6366f115', text: '#818cf8', border: '#6366f130' },
  RESOLVED: { bg: '#10b98115', text: '#34d399', border: '#10b98130' },
};

async function fetchHandoffs() {
  const convs = await conversationApi.list({ status: 'WAITING_FOR_AGENT', limit: 50 });
  const details = await Promise.all(convs.map((c) => conversationApi.get(c.id)));
  return details.filter((d) => d.handoff !== null);
}

export default function HandoffsPage() {
  const queryClient = useQueryClient();
  const [pulse, setPulse] = useState(false);

  const { data: conversations = [], isLoading } = useQuery({
    queryKey: ['handoffs'],
    queryFn: fetchHandoffs,
    refetchInterval: 15_000, // fallback poll every 15s
  });

  const withHandoffs = conversations.filter((c): c is ConvWithHandoff => c.handoff !== null);

  // ── Realtime: refresh list when a handoff is created or updated ──────────
  const handleHandoffNew = useCallback(() => {
    setPulse(true);
    void queryClient.invalidateQueries({ queryKey: ['handoffs'] });
    setTimeout(() => setPulse(false), 1200);
  }, [queryClient]);

  const handleHandoffUpdated = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['handoffs'] });
  }, [queryClient]);

  const handleConversationUpdated = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['handoffs'] });
  }, [queryClient]);

  usePortalSocketEvent('handoff:new', handleHandoffNew);
  usePortalSocketEvent('handoff:updated', handleHandoffUpdated);
  usePortalSocketEvent('conversation:updated', handleConversationUpdated);

  return (
    <div className="page">
      <PageGuide pageKey="handoffs" />

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>Handoff Queue</h1>
            {withHandoffs.length > 0 && (
              <span style={{
                padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                background: pulse ? '#ef444420' : '#f59e0b20',
                color: pulse ? '#f87171' : '#fbbf24',
                border: `1px solid ${pulse ? '#ef444440' : '#f59e0b40'}`,
                transition: 'all 0.3s',
              }}>
                {withHandoffs.length} open
              </span>
            )}
            {/* Live indicator */}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#34d399' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#34d399', display: 'inline-block', animation: 'livePulse 2s infinite' }} />
              Live
            </span>
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Conversations escalated to human operators · Updating in real-time</p>
        </div>
        <button
          onClick={() => void queryClient.invalidateQueries({ queryKey: ['handoffs'] })}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: 'transparent', border: '1px solid #1e2130', color: '#64748b', cursor: 'pointer' }}
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* ── Loading ── */}
      {isLoading && <div style={{ padding: '40px 0', textAlign: 'center' }}><Spinner size="md" /></div>}

      {/* ── Empty ── */}
      {!isLoading && withHandoffs.length === 0 && (
        <div style={{ padding: '56px 0', textAlign: 'center', border: '1px dashed #1e2130', borderRadius: 12, color: '#475569' }}>
          <CheckCircle2 size={32} style={{ opacity: 0.5, marginBottom: 12, color: '#34d399' }} />
          <p style={{ margin: 0, fontSize: 14 }}>No open handoffs — all conversations are handled by agents.</p>
        </div>
      )}

      {/* ── Cards ── */}
      {!isLoading && withHandoffs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {withHandoffs.map((conv) => {
            const pri = PRIORITY_STYLE[conv.handoff.priority] ?? PRIORITY_STYLE.MEDIUM;
            const sts = STATUS_STYLE[conv.handoff.status] ?? STATUS_STYLE.OPEN;
            const isUrgent = conv.handoff.priority === 'URGENT' || conv.handoff.priority === 'HIGH';
            return (
              <div
                key={conv.id}
                style={{
                  borderRadius: 12,
                  border: `1px solid ${isUrgent ? '#ef444440' : '#1e2130'}`,
                  background: '#0a0e1c',
                  overflow: 'hidden',
                  transition: 'border-color 0.15s',
                }}
              >
                {/* Card body — clickable to open conversation */}
                <Link
                  to="/conversations/$conversationId"
                  params={{ conversationId: conv.id }}
                  style={{ display: 'block', textDecoration: 'none', padding: '16px 20px' }}
                >
                  {/* Row 1: priority + status + time */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                    <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: pri.bg, color: pri.text, border: `1px solid ${pri.border}`, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <AlertTriangle size={10} /> {conv.handoff.priority}
                    </span>
                    <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: sts.bg, color: sts.text, border: `1px solid ${sts.border}` }}>
                      {conv.handoff.status}
                    </span>
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: '#475569' }}>
                      {new Date(conv.handoff.createdAt).toLocaleString('en-GB')}
                    </span>
                  </div>

                  {/* Row 2: agent + conv id */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: conv.handoff.note ? 8 : 10, fontSize: 13, color: '#e2e8f0' }}>
                    <MessageSquare size={14} style={{ color: '#64748b', flexShrink: 0 }} />
                    <span style={{ fontWeight: 600 }}>{conv.agent?.name ?? 'No agent'}</span>
                    <code style={{ fontSize: 11, color: '#475569', background: '#1e2130', padding: '1px 6px', borderRadius: 6 }}>{conv.id.slice(0, 8)}…</code>
                  </div>

                  {/* Handoff note */}
                  {conv.handoff.note && (
                    <p style={{ margin: '0 0 10px', fontSize: 12, color: '#94a3b8', lineHeight: 1.5, padding: '8px 12px', background: '#ffffff05', borderRadius: 8, border: '1px solid #1e2130' }}>
                      {conv.handoff.note}
                    </p>
                  )}

                  {/* Last message */}
                  {conv.messages.slice(-1).map((m) => (
                    <div key={m.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12, color: '#64748b', padding: '6px 10px', background: '#0f1221', borderRadius: 8, marginBottom: 10 }}>
                      <User size={12} style={{ flexShrink: 0, marginTop: 1 }} />
                      <span>{m.content.slice(0, 140)}{m.content.length > 140 ? '…' : ''}</span>
                    </div>
                  ))}

                  {/* Footer */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}>
                    <Clock size={12} />
                    <span>{new Date(conv.handoff.createdAt).toLocaleString('en-GB')}</span>
                    <span style={{ marginLeft: 'auto', color: '#6366f1', fontWeight: 600 }}>View conversation →</span>
                  </div>
                </Link>

                {/* Open in new tab button */}
                <div style={{ borderTop: '1px solid #12172a', padding: '8px 20px', display: 'flex', justifyContent: 'flex-end' }}>
                  <Link
                    to="/conversations/$conversationId"
                    params={{ conversationId: conv.id }}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, color: '#6366f1', textDecoration: 'none', background: '#6366f110', border: '1px solid #6366f130' }}
                  >
                    <ExternalLink size={11} /> Open in new tab
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style>{`@keyframes livePulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.3)} }`}</style>
    </div>
  );
}
